/**
 * Juni-2026 (User-Wunsch) — Sammel-Scan Fahrzeugpapiere per Datei-Upload + KI.
 *
 * Der Händler scannt die Papiere vieler Fahrzeuge (z. B. 50 Stück inkl.
 * Anschreiben) in EINE Datei (PDF oder Bild) und lädt sie hoch. Gemini Vision
 * erkennt ALLE enthaltenen Fahrzeuge (FIN + Stammdaten), gleicht sie mit dem
 * Bestand ab (Dublettenprüfung), bucht den Papiereingang bzw. legt neue
 * Entwurfs-Fahrzeuge an (interne Nummer mit Präfix „SCA-"). Funktioniert
 * analog zum ZB1-Scan beim Inserieren.
 */
import { useState, useRef } from "react";
import { X, Loader2, ScanLine, UploadCloud, Check, AlertTriangle, FilePlus2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

/**
 * Nachbearbeitungs-Liste für Sammel-Scan-Treffer „ohne FIN": pro Fahrzeug
 * kann die FIN manuell nachgetragen werden → bucht/legt das Fahrzeug an.
 */
function NeedsReviewList({ items }) {
    const [rows, setRows] = useState(() => items.map((it) => ({ ...it, fin: "", busy: false, done: null })));

    const setFin = (i, v) => setRows((p) => p.map((r, idx) => (idx === i ? { ...r, fin: v.toUpperCase() } : r)));

    const assign = async (i) => {
        const row = rows[i];
        const fin = (row.fin || "").replace(/[^A-Z0-9]/g, "");
        if (fin.length < 11) { toast.error("Bitte eine vollständige FIN eingeben (min. 11 Zeichen)."); return; }
        setRows((p) => p.map((r, idx) => (idx === i ? { ...r, busy: true } : r)));
        try {
            const r = await api.post("/dealer/dispo/papers-manual-assign", {
                vin: fin, brand: row.brand, model: row.model,
            });
            const label = r.data?.action === "matched" ? `gebucht (${r.data.internal_number || ""})` : `angelegt (${r.data.internal_number || ""})`;
            toast.success(`Fahrzeug ${label}.`);
            setRows((p) => p.map((rr, idx) => (idx === i ? { ...rr, busy: false, done: r.data } : rr)));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Zuordnung fehlgeschlagen.");
            setRows((p) => p.map((rr, idx) => (idx === i ? { ...rr, busy: false } : rr)));
        }
    };

    return (
        <div className="border border-amber-200 rounded-sm bg-amber-50/60" data-testid="papers-batch-needs-review">
            <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-800 border-b border-amber-200">
                Ohne erkennbare FIN — FIN bitte manuell nachtragen
            </div>
            <ul className="divide-y divide-amber-100">
                {rows.map((row, i) => (
                    <li key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`papers-batch-review-row-${i}`}>
                        <span className="text-[12px] text-[#111827] flex-1 min-w-0 truncate">
                            {[row.brand, row.model].filter(Boolean).join(" ") || "Unbekanntes Fahrzeug"}
                        </span>
                        {row.done ? (
                            <span className="text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1" data-testid={`papers-batch-review-done-${i}`}>
                                <Check className="w-3.5 h-3.5" /> {row.done.action === "matched" ? "Gebucht" : "Angelegt"} · {row.done.internal_number}
                            </span>
                        ) : (
                            <>
                                <Input value={row.fin} onChange={(e) => setFin(i, e.target.value)}
                                    placeholder="FIN eingeben…" className="h-8 w-52 text-xs font-mono-num rounded-sm"
                                    data-testid={`papers-batch-review-fin-${i}`} />
                                <Button type="button" onClick={() => assign(i)} disabled={row.busy}
                                    className="h-8 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-[11px] font-bold"
                                    data-testid={`papers-batch-review-assign-${i}`}>
                                    {row.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Übernehmen"}
                                </Button>
                            </>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function PapersBatchScanModal({ onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const pick = (f) => {
        if (!f) return;
        const okType = f.type === "application/pdf" || f.type.startsWith("image/");
        if (!okType) { toast.error("Bitte ein PDF oder Bild auswählen."); return; }
        if (f.size > 25 * 1024 * 1024) { toast.error("Datei zu groß (max 25 MB)."); return; }
        setFile(f);
        setResult(null);
    };

    const onDrop = (e) => {
        e.preventDefault();
        pick(e.dataTransfer?.files?.[0]);
    };

    const analyze = async () => {
        if (!file) { toast.error("Bitte zuerst eine Datei auswählen."); return; }
        setProcessing(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/dealer/dispo/papers-batch-ocr", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(r.data);
            const s = r.data?.summary || {};
            toast.success(`${s.found || 0} Fahrzeug(e) erkannt · ${s.matched || 0} gebucht · ${s.created || 0} neu`);
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sammel-Scan fehlgeschlagen.");
        } finally {
            setProcessing(false);
        }
    };

    const reset = () => { setFile(null); setResult(null); };

    const STAT = [
        { key: "found", label: "Erkannt", cls: "text-[#111827]", Icon: FileText },
        { key: "matched", label: "Gebucht", cls: "text-emerald-600", Icon: Check },
        { key: "created", label: "Neu angelegt", cls: "text-blue-600", Icon: FilePlus2 },
        { key: "needs_review", label: "Ohne FIN", cls: "text-amber-600", Icon: AlertTriangle },
    ];

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
            data-testid="papers-batch-scan-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="w-full max-w-2xl mt-10 bg-white border border-gray-200 rounded-sm shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black flex items-center gap-2">
                        <ScanLine className="w-5 h-5 text-[#FFCC00]" /> Sammel-Scan Fahrzeugpapiere
                    </h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" aria-label="Schließen" data-testid="papers-batch-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4 max-h-[68vh] overflow-y-auto">
                    {!result && (
                        <>
                            <p className="text-xs text-gray-500">
                                Laden Sie <strong>eine Datei</strong> (PDF oder Bild) mit den gescannten Papieren <strong>mehrerer Fahrzeuge</strong> hoch. Die KI erkennt automatisch jede Fahrgestellnummer (FIN) samt Stammdaten, bucht den Papiereingang bzw. legt neue Fahrzeuge an (Nummer „SCA-…").
                            </p>
                            <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden"
                                onChange={(e) => pick(e.target.files?.[0])} data-testid="papers-batch-file-input" />
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={onDrop}
                                className="w-full border-2 border-dashed border-gray-300 rounded-sm py-10 flex flex-col items-center gap-2 text-gray-500 hover:border-[#FFCC00] hover:bg-amber-50/40 transition-colors"
                                data-testid="papers-batch-dropzone"
                            >
                                <UploadCloud className="w-8 h-8" />
                                {file ? (
                                    <span className="text-sm font-bold text-[#111827]" data-testid="papers-batch-filename">{file.name}</span>
                                ) : (
                                    <>
                                        <span className="text-sm font-bold">Datei hierher ziehen oder klicken</span>
                                        <span className="text-[11px]">PDF oder Bild · max 25 MB</span>
                                    </>
                                )}
                            </button>
                            {processing && (
                                <div className="flex items-center gap-2 text-xs text-gray-600" data-testid="papers-batch-analyzing">
                                    <Loader2 className="w-4 h-4 animate-spin" /> KI analysiert das Dokument — dies kann je nach Umfang etwas dauern…
                                </div>
                            )}
                        </>
                    )}

                    {result && (
                        <div className="space-y-3" data-testid="papers-batch-result">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {STAT.map((s) => (
                                    <div key={s.key} className="rounded-sm border border-gray-200 px-3 py-2">
                                        <div className={`text-xl font-black font-mono-num ${s.cls}`}>{result.summary?.[s.key] || 0}</div>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{s.label}</div>
                                    </div>
                                ))}
                            </div>
                            {(result.summary?.activated || 0) > 0 && (
                                <div className="text-[11px] px-2 py-1.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="papers-batch-activated">
                                    <strong>{result.summary.activated}</strong> aus dem Vorlauf aktiviert — jetzt im öffentlichen Bestand verfügbar.
                                </div>
                            )}
                            {(result.created || []).length > 0 && (
                                <div className="border border-gray-200 rounded-sm overflow-auto max-h-52" data-testid="papers-batch-created-list">
                                    <table className="w-full text-[11px]">
                                        <thead className="bg-gray-100 text-gray-600 sticky top-0">
                                            <tr><th className="text-left px-2 py-1.5">Nr.</th><th className="text-left px-2 py-1.5">FIN</th><th className="text-left px-2 py-1.5">Fahrzeug</th></tr>
                                        </thead>
                                        <tbody>
                                            {result.created.map((c) => (
                                                <tr key={c.listing_id}>
                                                    <td className="px-2 py-1 font-mono-num font-bold text-[#111827]">{c.internal_number}</td>
                                                    <td className="px-2 py-1 font-mono-num">{c.vin}</td>
                                                    <td className="px-2 py-1 truncate">{c.title}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {(result.needs_review || []).length > 0 && (
                                <NeedsReviewList items={result.needs_review} />
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-sm">
                    {result ? (
                        <>
                            <Button type="button" variant="outline" onClick={reset} className="h-9 rounded-sm text-sm" data-testid="papers-batch-reset">Weitere Datei</Button>
                            <Button type="button" onClick={onClose} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="papers-batch-done">Fertig</Button>
                        </>
                    ) : (
                        <>
                            <Button type="button" variant="outline" onClick={onClose} className="h-9 rounded-sm text-sm" data-testid="papers-batch-cancel">Abbrechen</Button>
                            <Button type="button" onClick={analyze} disabled={processing || !file} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="papers-batch-analyze">
                                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ScanLine className="w-4 h-4 mr-1.5" />}
                                Erkennen &amp; verbuchen
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
